import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageTitle, useShop } from "@/context/ShopContext";
import { FABRIC_INFO } from "@/lib/fabricInfo";
import Reveal from "@/components/Reveal";

export default function FabricGuide() {
  usePageTitle("Fabric Guide");
  const { products } = useShop();

  return (
    <div data-testid="fabric-guide-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-16 md:pt-24 pb-10 md:pb-16">
        <Reveal>
          <p className="overline-label mb-4">Know Your Fabric</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">The LAMSAH Fabric Guide</h1>
          <p className="text-sm sm:text-base text-espresso/70 mt-5 max-w-2xl leading-relaxed">
            Colour is only half the story — the fabric decides how a hijab feels, drapes and catches the light.
            Learn the character of each material so you can choose with confidence.
          </p>
        </Reveal>
      </section>

      {FABRIC_INFO.map((f, i) => {
        const count = products.filter((p) => p.fabric === f.name).length;
        return (
          <section key={f.slug} data-testid={`guide-${f.slug}`} className={i % 2 === 1 ? "bg-white" : ""}>
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <Reveal>
                <div className={`overflow-hidden bg-beige aspect-[4/5] ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <img
                    src={f.image}
                    alt={`${f.name} hijab fabric texture and drape`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className={i % 2 === 1 ? "md:order-1" : ""}>
                  <p className="overline-label mb-3">{String(i + 1).padStart(2, "0")} — Fabric Library</p>
                  <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight">{f.name}</h2>
                  <p className="text-sm sm:text-base text-espresso/75 leading-relaxed mt-4 max-w-md">{f.intro}</p>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-8">
                    {f.specs.map(([k, v]) => (
                      <div key={k} className="border-b border-beige pb-3">
                        <dt className="text-[10px] uppercase tracking-[0.25em] text-taupe">{k}</dt>
                        <dd className="text-sm text-espresso/85 mt-1.5 leading-snug">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="text-xs text-taupe leading-relaxed mt-6">
                    <span className="uppercase tracking-[0.2em] text-[10px]">Care</span> — {f.care}
                  </p>
                  <Link
                    to={`/shop?fabric=${encodeURIComponent(f.name)}`}
                    data-testid={`guide-shop-${f.slug}`}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-espresso border-b border-espresso/30 pb-0.5 mt-8 hover:text-rose hover:border-rose/40 transition-colors"
                  >
                    {count > 0 ? `Shop ${count} ${f.name} ${count === 1 ? "piece" : "pieces"}` : `${f.name} — being curated`}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </section>
        );
      })}

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24 text-center" data-testid="guide-footer">
        <Reveal>
          <p className="font-arabic text-3xl text-rose" lang="ar" dir="rtl">لمسة</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight mt-4">More fabrics are joining the library</h2>
          <p className="text-sm sm:text-base text-espresso/70 mt-4 max-w-md mx-auto leading-relaxed">
            New materials are curated one touch at a time. Until then, browse the full colour catalogue.
          </p>
          <Link to="/shop" className="btn-primary mt-8" data-testid="guide-shop-all">
            Shop All Hijabs
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
